import { useCallback } from 'react';
import { CampaignListView, GrowthDashboardTopBarActions } from '@elva/ui';
import { useNavigate } from 'react-router-dom';
import { AsyncBoundary } from '@/components/feedback';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { viewProps } from '@/data/viewProps';
import { useNotify } from '@/data/notifications';
import { useCampaignList, useCampaignMutations } from '../hooks/useCampaignList';

function parseBudgetDollarsToCents(raw: string): number | null {
  const dollars = Number.parseFloat(raw.replace(/[$,\s]/g, ''));
  if (!Number.isFinite(dollars) || dollars <= 0) return null;
  const cents = Math.round(dollars * 100);
  if (cents < 50_000 || cents > 5_000_000) return null;
  return cents;
}

export function CampaignListPage() {
  const navigate = useNavigate();
  const notify = useNotify();

  usePageTopBar({
    actions: <GrowthDashboardTopBarActions />,
  });

  const { data, isPending, isError, error, refetch, isRefetching, isFetching } = useCampaignList();
  const { toggleActive, updateBudget } = useCampaignMutations();

  const backendIdFor = useCallback(
    (id: number) => data?.idMap[id],
    [data?.idMap],
  );

  const handleAdjustBudget = useCallback(
    (id: number) => {
      const backendId = backendIdFor(id);
      if (!backendId) return;
      const campaign = data?.items.find((c) => c.id === id);
      const current = campaign?.budget ? String(campaign.budget) : '';
      const raw = window.prompt(
        'Monthly budget in dollars ($500–$50,000)',
        current || '500',
      );
      if (raw == null) return;
      const budgetCents = parseBudgetDollarsToCents(raw);
      if (budgetCents == null) {
        notify.error(new Error('Budget must be between $500 and $50,000'));
        return;
      }
      updateBudget.mutate({ id: backendId, budgetCents });
    },
    [backendIdFor, data?.items, notify, updateBudget],
  );

  return (
    <AsyncBoundary
      loading={isPending}
      error={data === undefined && isError ? error : undefined}
      onRetry={refetch}
      retrying={isRefetching}
      isEmpty={data !== undefined && data.items.length === 0}
    >
      <CampaignListView
        {...viewProps({
          items: data?.items,
          headerStats: data?.headerStats,
          loading: isFetching || toggleActive.isPending || updateBudget.isPending,
          onToggleActive: (id: number, nextActive: boolean) => {
            const backendId = backendIdFor(id);
            if (backendId) toggleActive.mutate({ id, nextActive, backendId });
          },
          onNewCampaign: () => navigate('/campaigns/create'),
          onAdjustBudget: handleAdjustBudget,
          onViewLeads: () => navigate('/leads'),
          onViewReport: (id: number) => {
            const backendId = backendIdFor(id) ?? String(id);
            navigate(`/campaigns/${backendId}`);
          },
        })}
      />
    </AsyncBoundary>
  );
}
